import { Navigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface VendorGuardProps {
  children: React.ReactNode;
}

/**
 * VendorGuard - Protects routes that require vendor role access
 */
export const VendorGuard = ({ children }: VendorGuardProps) => {
  const { isAuthenticated, isVendor, isVendorApproved, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Redirect to dashboard if not vendor
  if (!isVendor()) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Show approval pending message if vendor is not approved
  if (!isVendorApproved()) {
    const approvalStatus = user?.vendorApprovalStatus || 'pending';
    const isRejected = approvalStatus === 'rejected';
    
    return (
      <div className="min-h-screen bg-background p-4">
        <Alert variant={isRejected ? 'destructive' : 'default'} className="max-w-2xl mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {isRejected ? 'Partner Account Rejected' : 'Partner Account Pending Approval'}
          </AlertTitle>
          <AlertDescription className="mt-2">
            {isRejected ? (
              <>
                Your partner account has been rejected. {user?.vendorApprovalNotes && (
                  <span className="block mt-2">Reason: {user.vendorApprovalNotes}</span>
                )}
                Please contact support for more information.
              </>
            ) : (
              <>
                Your partner account is pending admin approval. You cannot create or manage products and services until your account is approved.
                Please wait for an administrator to review and approve your account.
              </>
            )}
          </AlertDescription>
        </Alert>
        {children}
      </div>
    );
  }

  return <>{children}</>;
};